import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Info, Home, Sparkles, Truck, Repeat, Check, ArrowRight, Phone } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTABand } from "../components/Shared";
import { BUSINESS, GOOGLE } from "../lib/data";
import { GUIDE_LINKS } from "../lib/guides";

const PAGE_URL = "https://wisphomecleaning.ca/guides/house-cleaning-cost-hamilton";

const SIZE_RANGES = [
  { label: "2-bedroom home", range: "$175 – $250" },
  { label: "3-bedroom home", range: "$220 – $320" },
  { label: "Larger / executive homes (e.g. Ancaster, Dundas)", range: "$325+" },
];

const SERVICE_RANGES = [
  { icon: Repeat, label: "Recurring maintenance clean", desc: "Weekly, bi-weekly or monthly upkeep — often better value with regular visits.", range: "$175 – $320" },
  { icon: Sparkles, label: "One-time deep clean", desc: "A detailed top-to-bottom reset; usually 1.5–2× a standard clean.", range: "$400 – $650+" },
  { icon: Truck, label: "Move in / move out clean", desc: "Detailed, whole-home cleaning including inside cabinets and appliances.", range: "$400 – $700+" },
];

const FACTORS = [
  "The size and layout of your home — a little more space simply means a little more to care for.",
  "How much love it needs right now — sometimes a home has simply seen a lot of life, and the household hasn't had a chance to stay on top of the cleaning for a bit. That's completely okay; it just means a little more time and care to bring it back to a comfortable, happy place.",
  "How often we visit — regular cleans keep things easy to maintain, which is gentler on your budget.",
  "The kind of clean — a first-time or deep clean is naturally more involved than ongoing upkeep.",
  "The little extras that add real care and time — like cleaning inside the oven, fridge, cupboards, cabinets or closets. It's never about counting every small thing; it just helps us bring the right team and enough time so nothing ever feels rushed.",
  "Your home's own personality — pets, a bit of extra clutter, or any special touches you'd love us to focus on.",
];

const FAQS = [
  {
    q: "How much does house cleaning cost in Hamilton?",
    a: "Across the Hamilton area, professional cleaning typically ranges from about $175–$320 per visit for a 2–3 bedroom home, with one-time deep and move in/out cleans starting around $400. These are general market ranges — your exact price depends on your home and is confirmed after a free in-home visit.",
  },
  {
    q: "Why doesn't Wisp just list a fixed price?",
    a: "Because every home is genuinely different, a single sight-unseen number is rarely fair or accurate. We prefer to see your space in person first — it's a relaxed, no-obligation visit that lets us give you honest pricing with no surprises. We stay competitively priced with professional local services.",
  },
  {
    q: "Is a deep clean more expensive than a regular clean?",
    a: "Yes — a deep clean covers much more detail work (like inside appliances, cupboards and cabinets), so it usually costs about 1.5 to 2 times a standard maintenance clean. Many homes start with a deep clean, then move to a lighter, more affordable recurring rhythm.",
  },
];

export default function GuideCost() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "What Does House Cleaning Cost in Hamilton? An Honest 2026 Price Guide",
        author: { "@type": "Organization", name: "Wisp Home Cleaning Services" },
        publisher: {
          "@type": "Organization",
          name: "Wisp Home Cleaning Services",
          logo: { "@type": "ImageObject", url: "https://wisphomecleaning.ca/wisp-logo.png" },
        },
        datePublished: "2026-07-22",
        dateModified: "2026-07-22",
        mainEntityOfPage: PAGE_URL,
        image: "https://wisphomecleaning.ca/tagline-livingroom.jpg",
        description:
          "An honest guide to typical house cleaning prices across Hamilton, Ancaster, Dundas, Stoney Creek and surrounding areas — plus what actually affects your quote.",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
    []
  );

  return (
    <>
      <SEO
        title="What Does House Cleaning Cost in Hamilton? (2026 Price Guide)"
        description="An honest 2026 guide to typical house cleaning prices in Hamilton, Ancaster, Dundas, Stoney Creek & area — standard, deep and move-out ranges, and what affects your quote. Free in-home visit from Wisp."
        canonical="/guides/house-cleaning-cost-hamilton"
        jsonLd={jsonLd}
        image="/tagline-livingroom.jpg"
      />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white px-5 pt-16 pb-14 md:px-10">
        <div className="mx-auto max-w-4xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Local Price Guide · 2026</span>
          <h1 className="mt-3 font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            What does house cleaning cost in Hamilton?
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Wondering what a clean home should actually cost around here? You're not alone — and if you've called a few
            companies, you've probably heard some wildly different numbers. So let's talk it through, plainly and honestly,
            so you know what to expect across Hamilton, Ancaster, Dundas, Stoney Creek and nearby communities. And here's
            the short version up front: at Wisp, we use <span className="font-semibold text-slate-800">fair market
            pricing</span> — you'll never overpay, and we'll never cut corners just to look like the cheapest.
          </p>
        </div>
      </section>

      <section className="px-5 py-14 md:px-10">
        <div className="mx-auto max-w-4xl space-y-12">
          {/* Disclaimer */}
          <div className="flex items-start gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-6">
            <Info className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
            <p className="text-slate-700">
              <span className="font-semibold text-slate-900">A quick, honest note:</span> the ranges below are{" "}
              <span className="font-semibold">typical market ranges</span> across our area, shared simply to help you
              budget. Every home is different — so your exact price always follows a free in-home visit, with no surprises.
            </p>
          </div>

          {/* By size */}
          <div>
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">Typical cost by home size</h2>
            <p className="mt-3 text-slate-600">Per-visit ranges for a professional maintenance clean in the Hamilton area:</p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
              {SIZE_RANGES.map((r, i) => (
                <div
                  key={r.label}
                  data-testid="size-range-row"
                  className={`flex items-center justify-between gap-4 px-6 py-5 ${i % 2 ? "bg-muted/50" : "bg-white"}`}
                >
                  <span className="flex items-center gap-3 text-slate-700">
                    <Home className="h-5 w-5 text-primary" /> {r.label}
                  </span>
                  <span className="font-heading text-lg font-semibold text-slate-900">{r.range}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By service */}
          <div>
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">Typical cost by type of clean</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-3">
              {SERVICE_RANGES.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                  data-testid="service-range-card"
                  className="rounded-3xl border border-slate-200 bg-white p-6"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{s.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  <p className="mt-4 font-heading text-xl font-semibold text-primary">{s.range}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Factors */}
          <div>
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">What shapes your price</h2>
            <p className="mt-3 leading-relaxed text-slate-600">
              We think pricing should feel as honest and caring as the cleaning itself. So instead of a one-size number, we take a moment to understand what your home truly needs — that way you're only ever paying for the timing, effort and care it genuinely takes, and it always feels worth it.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">
              And it makes us happy to know we're helping make life just a little easier for someone great.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">A few gentle things shape your quote:</p>
            <ul className="mt-6 space-y-3">
              {FACTORS.map((f) => (
                <li key={f} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* The Wisp way */}
          <div className="rounded-3xl border border-slate-200 bg-muted p-8 md:p-10">
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">How Wisp prices — fairly, and with no surprises</h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Here's how we think about it: you deserve fair market pricing — right in line with the professional companies
              around us, never inflated and never a race to the bottom. So instead of guessing with a one-size-fits-all
              number, we pop by for a free, no-obligation visit, get to know your home and what matters most to you, and
              then give you an honest quote that's truly yours. No pressure and no haggling — just good, dependable work
              and a home you'll love coming back to.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href={BUSINESS.phoneHref}
                data-testid="guide-call-button"
                className="group flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-all duration-300 hover:-translate-y-0.5"
              >
                <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" /> Call or Text {BUSINESS.phone}
              </a>
              <Link
                to="/contact"
                data-testid="guide-contact-button"
                className="flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-3.5 font-semibold text-slate-700 transition-all duration-300 hover:border-primary hover:text-primary"
              >
                Book your free visit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <Sparkles className="h-4 w-4 text-secondary" /> Rated {GOOGLE.rating}★ by {GOOGLE.count} Hamilton-area neighbours.
            </p>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">Common questions about cost</h2>
            <div className="mt-6 space-y-4">
              {FAQS.map((f) => (
                <div key={f.q} data-testid="guide-faq" className="rounded-2xl border border-slate-200 bg-white p-6">
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{f.q}</h3>
                  <p className="mt-2 leading-relaxed text-slate-600">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-muted px-5 py-12 md:px-10">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-slate-900">More helpful guides</h2>
          <div className="mt-4 flex flex-col gap-2.5">
            {GUIDE_LINKS.filter((g) => g.slug !== "house-cleaning-cost-hamilton").map((g) => (
              <Link
                key={g.slug}
                to={g.path}
                data-testid={`more-guide-${g.slug}`}
                className="group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                {g.title}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>  );
}
